import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form } from '@anytag/shared-atoms';
import { SortOption } from '@anytag/shared-types';
import { PostListSortFieldV3 } from '@anytag/graphql';
import { getNumberValidFilter } from '@anytag/shared-utils';
import { SelectorField, SingleSelectorFieldProps, TextSearchField } from '@anytag/shared-molecules';
import { usePostsFilterOptions } from './hooks';
import { AdvancedFilter } from './AdvancedFilter';
import { PostsFilterFormKeys, PostsFilterFormValues } from './schemaTypes';

interface BasicFilterProps {
  onSubmit?: () => void;
  sortOptions: SortOption<PostListSortFieldV3>[];
}

export const BasicFilter = ({ sortOptions, onSubmit }: BasicFilterProps) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<PostsFilterFormValues>();
  const values = watch();
  const { allCountryOptions, socialMediaOptions, isCNCountrySupported } = usePostsFilterOptions();

  const basicFilterKeys: PostsFilterFormKeys[] = ['keyword', 'socialMedia', 'field', 'order', 'countryId'];
  const numOfValidFilter = getNumberValidFilter(values, basicFilterKeys);

  const handleSortChange: SingleSelectorFieldProps<PostsFilterFormKeys, SortOption<PostListSortFieldV3>>['onChange'] = (
    field,
    value,
    options
  ) => {
    const selectedOption = options.find((option) => option.value === value);
    setValue(field, value);
    setValue('field', selectedOption?.field || null);
    setValue('order', selectedOption?.order || null);
  };

  return (
    <Form.Fields>
      <TextSearchField<PostsFilterFormKeys>
        name="keyword"
        onSubmit={onSubmit}
        placeholder={t<string>('Search.PostContent')}
      />

      <SelectorField<PostsFilterFormKeys>
        clearable={false}
        name="socialMedia"
        onSubmit={onSubmit}
        className="max-w-42"
        options={socialMediaOptions}
        placeholder={t<string>('Selector.Social Media')}
        showAllOptions
      />

      <SelectorField<PostsFilterFormKeys>
        name="countryId"
        clearable={false}
        onSubmit={onSubmit}
        className="max-w-40"
        options={allCountryOptions}
        disabled={isCNCountrySupported}
        placeholder={t<string>('Selector.Country')}
      />

      <AdvancedFilter count={numOfValidFilter} onSubmit={onSubmit} />

      <SelectorField<PostsFilterFormKeys, SortOption<PostListSortFieldV3>>
        name="sort"
        onSubmit={onSubmit}
        className="max-w-40"
        options={sortOptions}
        onChange={handleSortChange}
        placeholder={t<string>('Sort')}
      />
    </Form.Fields>
  );
};
